// React
import { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import * as dayjs from 'dayjs';
import * as isoWeek from 'dayjs/plugin/isoWeek';

// Context.
import { InstanceContext } from '../../context/InstanceContext';
import { TimeRangeContext } from "../../context/TimeRangeContext";

// Types.
import InstancesDataTable from '../../types/instance/tables/InstancesDataTable';
import InstanceTarget from '../../types/instance/InstanceTarget';
import { ActivityId } from '../../types/instance/Activity';
import { StatisticId } from '../../types/instance/Statistic';
import { ChangeId } from '../../types/instance/Change';
import { EventId } from '../../types/instance/Event';
import { AlertId } from '../../types/instance/Alert';
import Tags from "../../types/Tags";

// Constants.
import { DATA_LOADED, DATA_LOADING, TARGET_COLOUR_CODES } from '../../component/Constants';

// Helpers.
import { archiverUrl, fetchResults, fetchWithAuthorization } from '../../helpers/utils';
import Helper from "../../helpers/Helper";
import { showError } from '../../utils/showAlert';

// Hooks
import { useInstanceFilters } from '../../hooks/useInstanceFilters';

// Components.
import InstancesTable from '../../component/instance/table/InstancesTable';
import InstancesOverTimeChart from '../../component/instance/chart/InstancesOverTimeChart';
import Breadcrumb from "../../container/breadcrumb";
import PageContent from '../../container/page_content';
import { Link, NavLink } from "react-router-dom";
import PeriodBreadcrumb from "../../component/PeriodBreadcrumb";
import HeaderActions from "../../container/header_actions";
import ConditionalRender from "../../helpers/ConditionalRender";
import FilterBar from "../../component/FilterBar";
import WidgetCard from "../../component/instance/widget";
import FilterPanel from '../../component/FilterPanel';

// DayJS plugins - note, the position of these in this file are important.
dayjs.extend(isoWeek.default);

function List(props: {
    toggleMenu: Function,
    applyPeriod: Function,
    setFilterOptions: Function,
    filterParameters: string
}) {
    const { instances } = useContext(InstanceContext);
    const timeRangeContext = useContext(TimeRangeContext)

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [chartLoading, setChartLoading] = useState<boolean>(true);
    const [tableLoading, setTableLoading] = useState<boolean>(true);
    const [tagsLoaded, setTagsLoaded] = useState<boolean>(true);
    const [instancesDataTable, setInstancesDataTable] = useState<InstancesDataTable[]>([]);
    const [tags, setTags] = useState<Tags[]>([]);
    
    // Use custom hook for filtering
    const {
        filters,
        updateFilter,
        clearAllFilters,
        availableFilterOptions,
        availableHostTypes,
        getFilteredInstances
    } = useInstanceFilters({
        instances: instances,
        shouldFetch: false
    });


    const comparisonSupported = useMemo(() => {
        return Helper.isComparisonSupported(timeRangeContext)
    }, [timeRangeContext]);

    const getTagValue = (list: Tags[], name: string) => {
        const getTag = list.find(item => item.tagname === name)
        return getTag ? getTag.tagvalue : ''
    }

    useEffect(function () {
        function createIdMap(array: any[]): any {
            // Key on id rather than index.
            let map: any = {};
            for (let i = 0; i < array.length; i++) {
                map[array[i].id] = array[i];
            }
            return map;
        }

        let active: boolean = true;

        async function load() {
            try {
                setLoading(true);
                setTagsLoaded(false);
                let promises: Promise<any>[] = [
                    fetchWithAuthorization(archiverUrl() + `/activity/id?limit=${process.env.REACT_APP_API_LIMIT}&${timeRangeContext.getTimeRangeQueryString()}&sort=waittime+desc`),
                    fetchWithAuthorization(archiverUrl() + `/statistic/id?limit=${process.env.REACT_APP_API_LIMIT}&${timeRangeContext.getTimeRangeQueryString()}&statistic=executions`),
                    fetchWithAuthorization(archiverUrl() + `/change/id?limit=${process.env.REACT_APP_API_LIMIT}&${timeRangeContext.getTimeRangeQueryString()}`),
                    fetchWithAuthorization(archiverUrl() + `/event/id?limit=${process.env.REACT_APP_API_LIMIT}&${timeRangeContext.getTimeRangeQueryString()}`),
                    fetchWithAuthorization(archiverUrl() + `/alert/id?limit=${process.env.REACT_APP_API_LIMIT}&${timeRangeContext.getTimeRangeQueryString()}`),
                    fetchWithAuthorization(archiverUrl() + `/datasource/tag`)
                ];

                if (comparisonSupported) {
                    promises.push(fetchWithAuthorization(archiverUrl() + `/activity/id?limit=${process.env.REACT_APP_API_LIMIT}&${timeRangeContext.getTimeRangeQueryString(true)}`));
                    promises.push(fetchWithAuthorization(archiverUrl() + `/statistic/id?limit=${process.env.REACT_APP_API_LIMIT}&${timeRangeContext.getTimeRangeQueryString(true)}&statistic=executions`));
                }

                const results: any[][] = await fetchResults(promises);

                if (active) {
                    const activity: ActivityId[] = results[0];
                    const statistics: StatisticId[] = results[1];
                    const changes: ChangeId[] = results[2];
                    const events: EventId[] = results[3];
                    const alerts: AlertId[] = results[4];
                    const tags: Tags[] = results[5];
                    let prevActivity: ActivityId[] = [];
                    let prevStatistics: StatisticId[] = [];
                    if (comparisonSupported) {
                        prevActivity = results[6];
                        prevStatistics = results[7];
                    }

                    const activityMap: any = createIdMap(activity);
                    const statisticMap: any = createIdMap(statistics);
                    const changeMap: any = createIdMap(changes);
                    const eventMap: any = createIdMap(events);
                    const alertMap: any = createIdMap(alerts);
                    const prevActivityMap: any = createIdMap(prevActivity);
                    const prevStatisticMap: any = createIdMap(prevStatistics);
                    const instanceMap: any = createIdMap(instances);

                    let data: InstancesDataTable[] = [];

                    // Add the active instances
                    for (let i = 0; i < activity.length; i++) {
                        const current: ActivityId = activity[i];
                        const executions: StatisticId = statisticMap[current.id];
                        const changes: ChangeId = changeMap[current.id];
                        const events: EventId = eventMap[current.id];
                        const alerts: AlertId = alertMap[current.id];
                        const previous: ActivityId = prevActivityMap[current.id];
                        const prevExecutions: StatisticId = prevStatisticMap[current.id];
                        const instance: InstanceTarget = instanceMap[current.id];
                        const currentInstanceTags: Tags[] = tags.filter(tag => tag.datasourceid === current.id);

                        if (instance) {
                            data.push({
                                id: current.id,
                                name: instance.name,
                                type: instance.type,
                                status: instance.status,
                                color: i < 10 ? TARGET_COLOUR_CODES[i] : '#FFFFFF',
                                changes: changes ? changes.count : 0,
                                alerts: alerts ? alerts.count : 0,
                                customEvents: events ? events.count : 0,
                                time: current ? current.waittime : null,
                                executions: executions ? executions.sum : null,
                                averageTime: current && executions ? current.waittime / executions.sum : null,
                                previousTime: previous ? previous.waittime : null,
                                previousExecutions: prevExecutions ? prevExecutions.sum : null,
                                previousAverageTime: previous && prevExecutions ? previous.waittime / prevExecutions.sum : null,
                                instance: instance,
                                // Add properties for each tag with tagname as the property name and value
                                ...tags.reduce((result, tag) => ({
                                    ...result,
                                    [tag.tagname]: getTagValue(currentInstanceTags, tag.tagname)
                                }), {})
                            });
                        }
                    }

                    // Append the idle instances
                    for (let i = 0; i < instances.length; i++) {
                        const instance: InstanceTarget = instances[i];
                        const currentInstanceTags: Tags[] = tags.filter(tag => tag.datasourceid === instance.id);

                        if (activityMap[instance.id]) {
                            continue;
                        }
                        if (instance) {
                            data.push({
                                id: instance.id,
                                name: instance.name,
                                type: instance.type,
                                status: instance.status,
                                color: '#FFFFFF',
                                changes: 0,
                                alerts: 0,
                                customEvents: 0,
                                time: 0,
                                executions: 0,
                                averageTime: null,
                                previousTime: null,
                                previousExecutions: null,
                                previousAverageTime: null,
                                instance: instance,
                                // Add properties for each tag with tagname as the property name and value
                                ...tags.reduce((result, tag) => ({
                                    ...result,
                                    [tag.tagname]: getTagValue(currentInstanceTags, tag.tagname)
                                }), {})
                            });
                        }
                    }

                    setInstancesDataTable(data);
                    setTableLoading(false);

                    const filteredTags: any = Helper.filterTagsByTagName(tags);
                    setTags(filteredTags);

                    // Set loading states after data is processed
                    setTagsLoaded(true);
                    setLoading(false);


                    // At this point would like to start a timer (setInterval)
                    // to update the instance statuses. But rebuilding the table
                    // with an initial sort order overrides the currently
                    // selected order.
                    // timeout = setInterval (() => updateTable (data), 30000);
                    // Could call (on InstanceContext):
                    // setInstances (await InstanceApi.getInstances ());
                    // and wait for useEffect to be triggered as it has a
                    // dependency on instances. This would fetch the data for
                    // the entire table, which may be slow.
                    // Instead just update the status as was done previously.
                }
            } catch (x: any) {
                console.error('Debug - Error loading data:', x);
                const errorMessage = x.message || x.toString();
                setError(errorMessage);
                showError('Failed to load instance data', errorMessage);
                setLoading(false);
                setTagsLoaded(true); // Set this to true even on error to prevent infinite loading
                setTableLoading(false);
                setInstancesDataTable([]);
            }
        }

        load();
        return function () {
            active = false;
        }
    }, [timeRangeContext, comparisonSupported]);

    const filteredInstances = useMemo(() => {
        if (loading) {
            return [];
        }

        // If tags aren't loaded but we have instances, show all instances
        if (!tagsLoaded && instancesDataTable.length > 0) {
            return instancesDataTable;
        }

        // Get filtered instance IDs from the hook
        const instanceTargets = instancesDataTable.map(row => row.instance as InstanceTarget);
        const filteredInstanceTargets = getFilteredInstances(instanceTargets);
        const filteredIds = new Set(filteredInstanceTargets.map(inst => inst.id));

        // Filter instancesDataTable based on filtered IDs
        return instancesDataTable.filter(row => filteredIds.has(row.id));
    }, [tagsLoaded, loading, instancesDataTable, getFilteredInstances]);

    const { alerts, changes, customEvents } = useMemo(() => {
        let alerts = 0, changes = 0, customEvents = 0;

        filteredInstances.forEach((instance, i) => {
            alerts += instance.alerts || 0;
            changes += instance.changes || 0;
            customEvents += instance.customEvents || 0;
            if (i < 10) {
                instance.color = TARGET_COLOUR_CODES[i];
            }
        });

        return { alerts, changes, customEvents };
    }, [filteredInstances]);

    const instancesOverTimeChart = useMemo(() => {
        return <InstancesOverTimeChart setLoading={setChartLoading}
                                       applyPeriod={props.applyPeriod}
                                       filters={props.filterParameters}
                                       instancesDataTable={filteredInstances}/>
    }, [filteredInstances]);


    return (
            <PageContent title='Instances - DBmarlin'
                         description='List of all monitored DB instances with high-level performance statistics'>
                {/* Heading */}
                <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                    <Breadcrumb heading="Instances"
                                type="instances">
                        <Link to="/instances">Analysis</Link>
                        <NavLink to="/instances" className={"current-breadcrumb-link"}>Instances</NavLink>
                        <PeriodBreadcrumb/>
                    </Breadcrumb>
                    <HeaderActions toggleMenu={props.toggleMenu} refresh={() => {
                    }}
                                   applyPeriod={props.applyPeriod} setFilterOptions={props.setFilterOptions}/>
                </div>

                <div className="col loader">
                    <ConditionalRender if={loading}>
                        <div className="bar"/>
                    </ConditionalRender>
                </div>

                {/* Filters */}
                <ConditionalRender if={props.setFilterOptions}>
                    {props.setFilterOptions && <FilterBar setFilterOptions={props.setFilterOptions}/>}
                </ConditionalRender>

                {/* Filter Panel - matching dashboard */}
                <FilterPanel
                        filterGroups={[
                            {
                                name: 'sensorstatus',
                                label: 'Sensor Status',
                                options: availableFilterOptions.sensorstatuses,
                                selectedValues: filters.sensorstatus || [],
                                onSelectionChange: (values: string[]) => updateFilter('sensorstatus', values)
                            },
                            {
                                name: 'type',
                                label: 'Type',
                                options: availableFilterOptions.types,
                                selectedValues: filters.type,
                                onSelectionChange: (values: string[]) => updateFilter('type', values)
                            },
                            {
                                name: 'tag',
                                label: 'Tag',
                                options: availableFilterOptions.tags,
                                selectedValues: filters.tag,
                                onSelectionChange: (values: string[]) => updateFilter('tag', values)
                            },
                            {
                                name: 'host',
                                label: 'Host',
                                options: availableFilterOptions.hosts,
                                selectedValues: filters.host,
                                onSelectionChange: (values: string[]) => updateFilter('host', values)
                            },
                            {
                                name: 'hostType',
                                label: 'Host Type',
                                options: availableHostTypes,
                                selectedValues: filters.hostType,
                                onSelectionChange: (values: string[]) => updateFilter('hostType', values)
                            }
                        ]}
                        onClearAll={clearAllFilters}
                        isLoading={chartLoading}
                />

                {/* Metric Widgets - Instances */}
                <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3">
                    <WidgetCard
                        name="Changes"
                        iconClassName="changes"
                        icon="fa-exchange"
                        value={changes}
                        cardTooltipText="The total auto-detected changes count"
                    />
                    <WidgetCard
                        name="Custom Events"
                        iconClassName="calendar"
                        icon="fa-calendar"
                        value={customEvents}
                        cardTooltipText="The total custom events count"
                    />
                    <WidgetCard
                        name="Alerts"
                        iconClassName="alerts"
                        icon="fa-exclamation-triangle"
                        value={alerts}
                        cardTooltipText="The total alerts count"
                    />
                </div>

                {instancesOverTimeChart}

                {/* Instances */}
                <div className="row row-cols-1">
                    <div className="col">
                        <div className="card collapsible">
                            <div className="card-header">
                                <i className="fal fa-database fa-fw" aria-hidden="true"/>
                                Monitored instances
                                <span className="badge bg-info"
                                      data-tip="Total instances count">{filteredInstances.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                   data-bs-target="#collapseInstances" aria-expanded="false"
                                   aria-controls="collapseInstances"/>
                            </div>
                            <ConditionalRender if={tableLoading}>
                                <div className="w-100 text-center text-muted  table-loader py-4">
                                    <div className="loader spinner">
                                    </div>
                                    <p className="mt-3">
                                        Loading data...
                                    </p>
                                </div>
                            </ConditionalRender>
                            <ConditionalRender if={!tableLoading}>
                                <div id="collapseInstances" className="card-body collapse show">
                                    <InstancesTable tags={tags} error={error}
                                                    loading={tableLoading ? DATA_LOADING : DATA_LOADED}
                                                    instancesDataTable={filteredInstances}/>
                                </div>
                            </ConditionalRender>
                        </div>
                    </div>
                </div>
            </PageContent>
    )
}

List.propTypes = {
    toggleMenu: PropTypes.func.isRequired,
    applyPeriod: PropTypes.func.isRequired
};

export default List;
